import { Injectable } from "@angular/core";
import { FgdsProductType } from "@domain/product/product";

import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { calculateDiscountedPrice } from "@domain/project/product-list/product-list-discount.util";

@Injectable({
  providedIn: "root",
})
export class CostService {
  calculateAlarmDeviceConfigurationsPrice(
    mapping: ConfigProductMap,
    productListQuantity = mapping.placedProductsCount,
    useZrefPrice = false,
  ) {
    return this.calculateConfigurationsPrice(mapping, [FgdsProductType.ALARMDEVICE], productListQuantity, useZrefPrice);
  }

  calculateGasWarningCenterConfigurationsPrice(
    mapping: ConfigProductMap,
    productListQuantity = mapping.placedProductsCount,
    useZrefPrice = false,
  ) {
    return this.calculateConfigurationsPrice(
      mapping,
      [FgdsProductType.GASWARNINGCENTER],
      productListQuantity,
      useZrefPrice,
      true,
    );
  }

  calculatePlasticSignConfigurationsPrice(
    mapping: ConfigProductMap,
    productListQuantity = mapping.placedProductsCount,
    useZrefPrice = false,
  ) {
    return this.calculateConfigurationsPrice(mapping, [FgdsProductType.PLASTICSIGN], productListQuantity, useZrefPrice);
  }

  calculateSignalElementConfigurationsPrice(
    mapping: ConfigProductMap,
    productListQuantity = mapping.placedProductsCount,
    useZrefPrice = false,
  ) {
    return this.calculateConfigurationsPrice(mapping, [FgdsProductType.SIGNALELEMENT], productListQuantity, useZrefPrice);
  }

  calculateTransmitterConfigurationsPrice(
    mapping: ConfigProductMap,
    productListQuantity = mapping.placedProductsCount,
    useZrefPrice = false,
  ) {
    return this.calculateConfigurationsPrice(
      mapping,
      [FgdsProductType.TRANSMITTER, FgdsProductType.SENSOR],
      productListQuantity,
      useZrefPrice,
    );
  }

  calculateFlameDetectorConfigurationsPrice(
    mapping: ConfigProductMap,
    productListQuantity = mapping.placedProductsCount,
    useZrefPrice = false,
  ) {
    return this.calculateConfigurationsPrice(mapping, [FgdsProductType.FLAME_DETECTOR], productListQuantity, useZrefPrice);
  }

  private calculateConfigurationsPrice(
    mapping: ConfigProductMap,
    productTypes: readonly FgdsProductType[],
    productListQuantity: number,
    useZrefPrice: boolean,
    multiplyByProductQuantity = false,
  ): number {
    let price: number = 0;
    mapping.data.forEach((productData) => {
      if (productTypes.some((productType) => productType === productData.type)) {
        const discountedPrice = this.calculateDiscountedPrice(productData, useZrefPrice);
        const quantity = multiplyByProductQuantity ? productData.quantity : 1;
        price += discountedPrice != null ? discountedPrice * productListQuantity * quantity : 0;
      }
      if (productData.type === FgdsProductType.ATTACHMENT) {
        price += this.calculateAttachmentCostsForProductData(productData, productListQuantity, useZrefPrice);
      }
    });
    return price;
  }

  private calculateDiscountedPrice(productData: ProductData, useZrefPrice: boolean): number | undefined {
    const priceData = useZrefPrice ? productData.zrefPriceData : productData.priceData;
    return priceData ? calculateDiscountedPrice(priceData.price, productData.discount ?? 0) : undefined;
  }

  private calculateAttachmentCostsForProductData(
    attachmentProduct: ProductData,
    productListQuantity: number,
    useZrefPrice = false,
  ): number {
    let sum = 0;
    if (!attachmentProduct.isAvailable) {
      return sum;
    }
    const discountedPrice = this.calculateDiscountedPrice(attachmentProduct, useZrefPrice);
    if (discountedPrice != null) {
      sum += discountedPrice * productListQuantity * attachmentProduct.quantity;
    }
    return sum;
  }
}
